import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Input, Select, message, Typography,
  Switch, Popconfirm, Form, Rate, Image, Drawer,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, StarOutlined,
} from '@ant-design/icons';
import { adminAPI, toolsAPI, categoriesAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import type { Tool, Category } from '../types';

const { Title, Text } = Typography;

const AdminToolManagePage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [tools, setTools] = useState<Tool[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>();
  const [pricingFilter, setPricingFilter] = useState<string | undefined>();

  const [editDrawer, setEditDrawer] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [form] = Form.useForm();

  const fetchTools = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, limit: pagination.limit, sort: 'id', order: 'DESC' };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (pricingFilter) params.pricing_type = pricingFilter;
      const res = await adminAPI.getTools(params);
      setTools(res.data.tools);
      setPagination(prev => ({
        ...prev,
        page: res.data.pagination.page,
        total: res.data.pagination.total,
      }));
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, pricingFilter, pagination.limit, t]);

  const fetchCategories = async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data.categories || []);
    } catch {}
  };

  useEffect(() => {
    fetchTools();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (tool: Tool) => {
    setEditingTool(tool);
    form.setFieldsValue({
      name: tool.name,
      slug: tool.slug,
      short_description: tool.short_description,
      description: tool.description,
      website_url: tool.website_url,
      category_id: tool.category_id,
      pricing_type: tool.pricing_type,
      tags: tool.tags,
      is_featured: tool.is_featured,
    });
    setEditDrawer(true);
  };

  const handleCreate = () => {
    setEditingTool(null);
    form.resetFields();
    form.setFieldsValue({ pricing_type: 'free', is_featured: false });
    setEditDrawer(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingTool) {
        await toolsAPI.updateTool(editingTool.id, values);
        message.success(t('adminPanel.updateSuccess'));
      } else {
        await toolsAPI.createTool(values);
        message.success(t('adminPanel.createSuccess'));
      }
      setEditDrawer(false);
      fetchTools(pagination.page);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('adminPanel.updateFailed'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await toolsAPI.deleteTool(id);
      message.success(t('adminPanel.deleteSuccess'));
      fetchTools(pagination.page);
    } catch {
      message.error(t('adminPanel.deleteFailed'));
    }
  };

  const pricingColors: Record<string, string> = {
    free: 'green',
    freemium: 'blue',
    paid: 'gold',
    contact: 'purple',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('adminPanel.colLogo'),
      dataIndex: 'logo',
      width: 60,
      render: (logo: string) => (
        <Image
          src={logo || '/tool-logos/default.png'}
          width={32}
          height={32}
          style={{ borderRadius: 6, objectFit: 'cover' }}
          preview={false}
          fallback="/tool-logos/default.png"
        />
      ),
    },
    {
      title: t('admin.colName'),
      dataIndex: 'name',
      width: 200,
      render: (name: string, record: Tool) => (
        <div>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>{record.slug}</Text>
        </div>
      ),
    },
    {
      title: t('adminPanel.colCategory'),
      key: 'category',
      width: 130,
      render: (_: any, record: Tool) => (
        <Tag>{record.category?.name || '-'}</Tag>
      ),
    },
    {
      title: t('tools.pricing'),
      dataIndex: 'pricing_type',
      width: 100,
      render: (v: string) => <Tag color={pricingColors[v]}>{t(`pricing.${v}`)}</Tag>,
    },
    {
      title: t('adminPanel.colRating'),
      dataIndex: 'rating',
      width: 100,
      render: (v: number) => <Rate disabled value={v} style={{ fontSize: 12 }} />,
    },
    {
      title: t('adminPanel.colViews'),
      dataIndex: 'view_count',
      width: 80,
      sorter: (a: Tool, b: Tool) => a.view_count - b.view_count,
    },
    {
      title: t('adminPanel.colFeatured'),
      dataIndex: 'is_featured',
      width: 80,
      render: (v: boolean) => v ? <StarOutlined style={{ color: '#faad14', fontSize: 16 }} /> : '-',
    },
    {
      title: t('admin.colAvailability'),
      dataIndex: 'availability_cn',
      width: 100,
      render: (v: string) => {
        const colors: Record<string, string> = {
          cn_native: 'green', direct: 'cyan', mirror: 'blue', vpn: 'orange', blocked: 'red', unknown: 'default',
        };
        return <Tag color={colors[v] || 'default'}>{v}</Tag>;
      },
    },
    {
      title: t('admin.colActions'),
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Tool) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title={t('adminPanel.confirmDelete')} onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>{t('adminPanel.toolManagement')}</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchTools(1)}>{t('admin.refresh')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('adminPanel.createTool')}
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Space wrap>
          <Input
            placeholder={t('admin.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={() => fetchTools(1)}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder={t('adminPanel.colCategory')}
            value={categoryFilter}
            onChange={v => setCategoryFilter(v)}
            allowClear
            style={{ width: 160 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={categories.map(c => ({ label: c.name_zh || c.name, value: c.id }))}
          />
          <Select
            placeholder={t('tools.pricing')}
            value={pricingFilter}
            onChange={v => setPricingFilter(v)}
            allowClear
            style={{ width: 120 }}
            options={[
              { label: t('pricing.free'), value: 'free' },
              { label: t('pricing.freemium'), value: 'freemium' },
              { label: t('pricing.paid'), value: 'paid' },
              { label: t('pricing.contact'), value: 'contact' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchTools(1)}>
            {t('common.search')}
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table
          loading={loading}
          dataSource={tools}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showTotal: total => t('admin.totalItems').replace('{total}', String(total)),
            onChange: page => fetchTools(page),
          }}
        />
      </Card>

      {/* Edit / Create Drawer */}
      <Drawer
        title={editingTool ? `${t('adminPanel.editTool')} - ${editingTool.name}` : t('adminPanel.createTool')}
        open={editDrawer}
        onClose={() => setEditDrawer(false)}
        width={560}
        extra={
          <Button type="primary" onClick={handleSave}>{t('admin.save')}</Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('admin.colName')} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Slug" name="slug" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('adminPanel.colShortDesc')} name="short_description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label={t('adminPanel.colDescription')} name="description">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label={t('adminPanel.colWebsite')} name="website_url" rules={[{ required: true, type: 'url' }]}>
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item label={t('adminPanel.colCategory')} name="category_id">
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={categories.map(c => ({ label: c.name_zh || c.name, value: c.id }))}
              allowClear
            />
          </Form.Item>
          <Form.Item label={t('tools.pricing')} name="pricing_type">
            <Select
              options={[
                { label: t('pricing.free'), value: 'free' },
                { label: t('pricing.freemium'), value: 'freemium' },
                { label: t('pricing.paid'), value: 'paid' },
                { label: t('pricing.contact'), value: 'contact' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Tags" name="tags">
            <Input placeholder="tag1, tag2, tag3" />
          </Form.Item>
          <Form.Item label={t('adminPanel.colFeatured')} name="is_featured" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminToolManagePage;
